import { useEffect, useRef } from "react"

import { ACTION_NATIVE } from "@/modules/helpers/enum"
import { useAuthStore } from "@/modules/helpers/hooks/shared"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { useLayoutStore } from "@/modules/helpers/hooks/shared"
import { useNavigationLeft } from "@/modules/helpers/hooks/shared"
import { useShowCampaign } from "@/modules/helpers/hooks/shared"
import { redirectLinkNotification } from "@/modules/helpers/hooks/use-notification-redirect"
import { sendTokenSync } from "@/modules/helpers/utils"
import { isDevMode } from "@/modules/helpers/utils/env"
import { safeJsonParse } from "@/modules/helpers/utils/safe-json"

// interface TokenMessage {
//   type: "SYNC_TOKEN" | "GET_TOKEN" | "LOGOUT" | "TOKEN_RESPONSE"
//   accessToken?: string
//   refreshToken?: string
//   appVersion?: string
//   insetTop?: number
//   insetBottom?: number
//   isNewUser?: boolean
// }

export const useTokenSync = () => {
  const { accessToken, refreshToken, login } = useAuthStore()
  const { loadLayout } = useLayoutStore()
  const { setPermissions } = useCurrentUser()
  const { setOpen } = useNavigationLeft()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasRequestedTokenRef = useRef(false)
  const isProcessingRef = useRef(false)
  useEffect(() => {
    // Chỉ request token nếu chưa từng request và không đang process
    if (!isDevMode && !accessToken && !hasRequestedTokenRef.current && !isProcessingRef.current) {
      hasRequestedTokenRef.current = true

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        console.log("🔑 Requesting token sync...")
        sendTokenSync()
      }, 1000)
    }

    // Lắng nghe message từ React Native WebView
    const handleMessage = (event: MessageEvent) => {
      const data = safeJsonParse<any>(event.data)
      if (!data) return

      try {
        switch (data.type) {
          case ACTION_NATIVE.CAMERA_PERMISSION_DENIED: {
            window.dispatchEvent(
              new CustomEvent("openSettings", {
                detail: {
                  type: "CAMERA_PERMISSION_DENIED"
                }
              })
            )
            break
          }
          case ACTION_NATIVE.NOTIFICATION_ACTION: {
            const redirectData = redirectLinkNotification(data.payload.notification)
            if (redirectData) {
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("navigationAction", {
                    detail: redirectData
                  })
                )
              }, 100)
            }
            break
          }

          case ACTION_NATIVE.SYNC_PERMISSION: {
            setPermissions(data?.permissions)
            break
          }
          case ACTION_NATIVE.PING: {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: "PONG"
              })
            )
            break
          }
          case ACTION_NATIVE.DEEP_LINK_CAMPAIGN: {
            const campaignId = data?.campaignId as string
            if (campaignId) {
              useShowCampaign.getState().setId(campaignId)
            }
            break
          }
          case ACTION_NATIVE.SYNC_TOKEN: {
            isProcessingRef.current = true

            const notificationToken =
              data.expoPushToken ||
              data.notificationToken ||
              data.fcmToken ||
              data.notiToken ||
              undefined
            const currentState = useAuthStore.getState()

            // Login chỉ dựa access/refresh — không block / không re-login vì noti token
            if (data.accessToken && data.refreshToken) {
              if (
                data.accessToken !== currentState.accessToken ||
                data.refreshToken !== currentState.refreshToken
              ) {
                login({
                  accessToken: data.accessToken,
                  refreshToken: data.refreshToken,
                  notificationToken: notificationToken ?? currentState.notificationToken,
                  isNewUser: data.isNewUser
                })
              } else if (
                notificationToken &&
                notificationToken !== currentState.notificationToken
              ) {
                useAuthStore.getState().setNotificationToken(notificationToken)
              }
            } else if (
              notificationToken &&
              notificationToken !== currentState.notificationToken
            ) {
              useAuthStore.getState().setNotificationToken(notificationToken)
            }

            loadLayout({
              insetTop: data.insetTop || 0,
              insetBottom: data.insetBottom || 0
            })

            setOpen(false)

            // Confirm WebView is alive after processing token
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: "WEBVIEW_ALIVE" }))
            }

            // Reset flags sau khi xử lý xong
            setTimeout(() => {
              isProcessingRef.current = false
            }, 100)
            break
          }
          case ACTION_NATIVE.SYNC_NOTIFICATION_TOKEN:
          case ACTION_NATIVE.SYNC_FCM_TOKEN: {
            const token =
              data.expoPushToken ||
              data.notificationToken ||
              data.fcmToken ||
              data.notiToken ||
              data.token
            if (token) {
              useAuthStore.getState().setNotificationToken(token)
            }
            break
          }
        }
      } catch (error) {
        console.error("Error parsing message from React Native:", error)
        isProcessingRef.current = false
      }
    }

    // Reset flag khi đã có token
    if (accessToken) {
      hasRequestedTokenRef.current = false
      isProcessingRef.current = false

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    // Check localStorage for pending deep link (native may have set it before this listener mounted)
    const pendingCampaignId = localStorage.getItem("pendingDeepLinkCampaignId")
    if (pendingCampaignId) {
      localStorage.removeItem("pendingDeepLinkCampaignId")
      useShowCampaign.getState().setId(pendingCampaignId)
    }

    // Lắng nghe message từ window
    window.addEventListener("message", handleMessage)

    return () => {
      window.removeEventListener("message", handleMessage)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [accessToken, refreshToken, login, loadLayout, setOpen, setPermissions])
}
